import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import HeaderWithIcon from '../../components/common/HeaderWithIcon';
import toast from 'react-hot-toast';
import { gamesApi } from '../../api/games';
import { settingsApi } from '../../api/settings';
import { Plus, Trash2, X } from 'lucide-react';
import getGameType from '../../utils/games';
import getGameImage from '../../utils/getGameImage';

export default function HomePageSettings() {
  const { tokens, user } = useAuth();
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [homepageGames, setHomepageGames] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfigPopup, setShowConfigPopup] = useState(false);
  const [gameConfig, setGameConfig] = useState({
    commissionRate: 0,
    winRate: 0,
    maxBet: 0,
    minBet: 0,
  });

  const fetchGames = async () => {
    try {
      const response = await gamesApi.getAllGames(tokens.access.token);
      if (response.status === 200) {
        setGames(response.data);
      } else {
        toast.error('Failed to fetch games');
      }
    } catch (error) {
      console.error('Error fetching games:', error);
      toast.error('Failed to fetch games');
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsApi.getSettings(
        tokens.access.token,
        user.id,
        'homepage_games'
      );
      if (response.status === 200) {
        // Handle the new response format where data is an object with otherSettings.games
        if (
          response.data &&
          response.data.otherSettings &&
          response.data.otherSettings.games
        ) {
          setHomepageGames(response.data.otherSettings.games);
        } else {
          setHomepageGames([]);
        }
      } else if (response.status === 204) {
        setHomepageGames([]);
      } else {
        toast.error('Failed to fetch homepage games');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to fetch homepage games');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setLoading(true);
      // Create payload with each game's configuration
      const response = await settingsApi.settings(
        tokens.access.token,
        user.id,
        'homepage_games',
        'homepage_games_config',
        {
          games: homepageGames
        }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success('Homepage games saved successfully');
      } else {
        toast.error('Failed to save homepage games');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save homepage games');
    } finally {
      setLoading(false);
    }
  };

  const handleGameSelect = (game) => {
    setSelectedGame(game);
    setDropdownOpen(false);
    // Initialize config with default values
    setGameConfig({
      commissionRate: 0,
      winRate: 0,
      maxBet: 0,
      minBet: 0,
    });
    setShowConfigPopup(true);
  };

  const addGame = () => {
    if (!selectedGame) {
      toast.error('Please select a game');
      return;
    }

    // Check if game is already added
    if (homepageGames.some((game) => game.id === selectedGame.id)) {
      toast.error('This game is already added to homepage');
      return;
    }

    // Add the game with its configuration
    const gameWithConfig = {
      ...selectedGame,
      gameConfig: {
        ...gameConfig
      }
    };

    setHomepageGames([...homepageGames, gameWithConfig]);
    setSelectedGame(null);
    setShowConfigPopup(false);
  };

  const removeGame = (gameId) => {
    setHomepageGames(homepageGames.filter((game) => game.id !== gameId));
  };

  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setGameConfig({
      ...gameConfig,
      [name]: parseFloat(value),
    });
  };

  useEffect(() => {
    fetchGames();
    fetchSettings();
  }, [tokens.access.token, user.id]);

  return (
    <div className='min-h-screen relative overflow-hidden'>
      <HeaderWithIcon title='Home Page Settings' />

      <div className='p-5'>
        <div className='mb-6'>
          <h2 className='text-lg font-semibold mb-2'>Homepage Games</h2>
          <p className='text-sm text-gray-600 mb-4'>
            Select games to display on the homepage. These games will be shown
            to users when they visit your site.
          </p>

          <div className='flex items-end gap-3 mb-4'>
            <div className='flex-1 relative'>
              <label className='block text-sm text-gray-700 mb-1'>
                Select Game
              </label>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className='w-full p-2.5 text-sm border border-gray-300 rounded-md flex justify-between items-center bg-white'
              >
                <span>
                  {selectedGame ? selectedGame.name : 'Select a game'}
                </span>
                <span className='text-gray-500'>
                  {dropdownOpen ? '▲' : '▼'}
                </span>
              </button>

              {dropdownOpen && (
                <ul className='absolute z-10 w-full mt-1 bg-white border rounded-md shadow-md max-h-60 overflow-y-auto'>
                  {games.map((game) => (
                    <li
                      key={game.id}
                      onClick={() => handleGameSelect(game)}
                      className='p-2.5 text-sm hover:bg-gray-100 cursor-pointer'
                    >
                      {game.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className='mb-6'>
          <h3 className='text-md font-medium mb-3'>Selected Homepage Games</h3>

          {homepageGames.length === 0 ? (
            <div className='p-4 border border-dashed border-gray-300 rounded-md text-center text-gray-500'>
              No games selected for homepage. Add games above.
            </div>
          ) : (
            <div className='space-y-3'>
              {homepageGames.map((game) => {
                const gameType = getGameType(game.otherSettings?.image);
                const GameImage = getGameImage(gameType);

                return (
                  <div
                    key={game.id}
                    className='flex items-center justify-between p-3 border border-gray-200 rounded-md'
                  >
                    <div className='flex items-center gap-3'>
                      <div className='w-10 h-10 rounded-md overflow-hidden bg-gray-100'>
                        {GameImage && (
                          <img
                            src={GameImage}
                            className='w-full h-full object-cover'
                          />
                        )}
                      </div>
                      <div>
                        <h4 className='font-medium'>{game.name}</h4>
                        <div className='text-xs text-gray-500 space-y-1'>
                          <div>Commission Rate: {game.gameConfig?.commissionRate || 0}%</div>
                          <div>Win Rate: {game.gameConfig?.winRate || 0}%</div>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => removeGame(game.id)}
                      className='text-red-500 hover:text-red-700'
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className='flex justify-end'>
          <button
            onClick={saveSettings}
            disabled={loading}
            className={`px-4 py-2 bg-[#0E64D1] text-white rounded-md ${
              loading ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Game Configuration Popup */}
      {showConfigPopup && selectedGame && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg p-6 w-full max-w-md'>
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-lg font-semibold'>Configure {selectedGame.name}</h3>
              <button 
                onClick={() => setShowConfigPopup(false)}
                className='text-gray-500 hover:text-gray-700'
              >
                <X size={20} />
              </button>
            </div>
            
            <div className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Commission Rate (%)
                </label>
                <input
                  type='number'
                  name='commissionRate'
                  value={gameConfig.commissionRate}
                  onChange={handleConfigChange}
                  className='w-full p-2 border border-gray-300 rounded-md'
                  min='0'
                  max='100'
                  step='0.1'
                />
              </div>
              
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Win Rate (%)
                </label>
                <input
                  type='number'
                  name='winRate'
                  value={gameConfig.winRate}
                  onChange={handleConfigChange}
                  className='w-full p-2 border border-gray-300 rounded-md'
                  min='0'
                  max='100'
                  step='0.1'
                />
              </div>
              
              {/* <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Minimum Bet ($)
                </label>
                <input
                  type='number'
                  name='minBet'
                  value={gameConfig.minBet}
                  onChange={handleConfigChange}
                  className='w-full p-2 border border-gray-300 rounded-md'
                  min='0'
                  step='1'
                />
              </div>
              
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Maximum Bet ($)
                </label>
                <input
                  type='number'
                  name='maxBet'
                  value={gameConfig.maxBet}
                  onChange={handleConfigChange}
                  className='w-full p-2 border border-gray-300 rounded-md'
                  min='0'
                  step='10'
                />
              </div> */}
            </div>
            
            <div className='mt-6 flex justify-end space-x-3'>
              <button
                onClick={() => setShowConfigPopup(false)}
                className='px-4 py-2 text-gray-700 border border-gray-300 rounded-md'
              >
                Cancel
              </button>
              <button
                onClick={addGame}
                className='px-4 py-2 bg-[#0E64D1] text-white rounded-md'
              >
                Add Game
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
